import { TrendingDown } from "lucide-react";
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Line } from "recharts";

export const PnlByAssetChart = ({ data }) => {
  if (!data || !data.chart_data || data.chart_data.length === 0 || !data.assets || data.assets.length === 0) return null;

  const { chart_data, assets } = data;
  const filteredAssets = assets.filter(a => a.current_pnl !== 0 && a.current_pnl !== null);
  if (filteredAssets.length === 0) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [, month, day] = dateStr.split('-');
    return `${day}/${month}`;
  };

  const AssetTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const nonZeroPayload = payload.filter(p => p.value !== 0);
      if (nonZeroPayload.length === 0) return null;
      return (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-gray-400 text-sm mb-2">{label}</p>
          {nonZeroPayload.map((entry) => (
            <div key={entry.dataKey || entry.name} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-gray-300 text-sm">{entry.name}</span>
              </div>
              <span className={`font-semibold text-sm ${entry.value >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {entry.value >= 0 ? '+' : ''}{entry.value?.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})} USDT
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-[#0d0d0f] rounded-xl border border-gray-800 p-6 mb-8" data-testid="pnl-by-asset-chart">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <TrendingDown className="w-5 h-5 text-red-400" />
          <h3 className="text-lg font-semibold text-white">Evolución PNL No Realizado por Activo</h3>
        </div>
        <div className="flex flex-wrap gap-4">
          {filteredAssets.map((asset) => (
            <div key={asset.asset} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: asset.color }} />
              <span className="text-gray-400 text-sm">{asset.asset}</span>
              <span className={`text-xs font-medium ${asset.current_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                ({asset.current_pnl >= 0 ? '+' : ''}{asset.current_pnl?.toLocaleString('es-ES', {minimumFractionDigits: 0, maximumFractionDigits: 0})})
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chart_data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
            <XAxis dataKey="date" stroke="#6b7280" fontSize={12} tickFormatter={formatDate} interval="preserveStartEnd" />
            <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(value) => `${value >= 0 ? '+' : ''}${(value / 1000).toFixed(0)}k`} />
            <Tooltip content={<AssetTooltip />} />
            <Line type="monotone" dataKey={() => 0} stroke="#374151" strokeWidth={1} strokeDasharray="5 5" dot={false} isAnimationActive={false} />
            {filteredAssets.map((asset) => (
              <Line key={asset.asset} type="monotone" dataKey={asset.asset} name={asset.asset} stroke={asset.color} strokeWidth={2.5} dot={false} activeDot={{ r: 6, strokeWidth: 2 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-gray-500 text-xs mt-4">
        * Muestra la evolución del PNL no realizado desde la fecha de compra hasta hoy, usando precios históricos de MEXC.
      </p>
    </div>
  );
};
